import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';
import {
  MarketplaceListing,
  SellerProfile,
  MyListing,
  SavedListing,
  UserMarketplaceStats,
} from '@/types/residents/marketplaceListing';
import { MARKETPLACE_API, CACHE_TAGS } from '@/constants/residents/marketplaceConstants';
import {
  ApiResponse,
  BackendProfileResponse,
} from './types';

// ============================================================================
// Profile API
// ============================================================================

export const profileApi = createApi({
  reducerPath: 'marketplaceProfileApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    CACHE_TAGS.Profile,
    CACHE_TAGS.SellerProfile,
    CACHE_TAGS.MyListings,
    CACHE_TAGS.SavedItems,
    CACHE_TAGS.RecentlyViewed,
    CACHE_TAGS.Listing,
  ],
  endpoints: (builder) => ({
    /**
     * Get current user's marketplace profile stats
     */
    getMyProfile: builder.query<UserMarketplaceStats, void>({
      query: () => ({
        url: MARKETPLACE_API.profile.base,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<BackendProfileResponse> | null): UserMarketplaceStats => {
        if (!response || !response.data) {
          console.warn('getMyProfile: Empty or null response from API');
          return {
            bought: 0,
            sold: 0,
            followers: 0,
            following: 0,
            rating: 0,
            reviewCount: 0,
          };
        }
        return {
          bought: response.data.boughtCount || 0,
          sold: response.data.soldCount || 0,
          followers: response.data.followersCount || 0,
          following: response.data.followingCount || 0,
          rating: response.data.rating || 0,
          reviewCount: response.data.reviewCount || 0,
        };
      },
      providesTags: [{ type: CACHE_TAGS.Profile, id: 'ME' }],
    }),

    /**
     * Get another user's seller profile
     */
    getSellerProfile: builder.query<SellerProfile | null, string>({
      query: (userId) => ({
        url: MARKETPLACE_API.profile.byUserId(userId),
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<any> | null): SellerProfile | null => {
        if (!response || !response.data) {
          console.warn('getSellerProfile: Empty or null response from API');
          return null;
        }
        const data = response.data;
        return {
          id: String(data.accountId),
          name: data.name || 'Unknown User',
          initials: data.initials || 'U',
          avatarUrl: data.avatarUrl,
          rating: data.rating || 0,
          reviewCount: data.reviewCount || 0,
          bought: data.boughtCount || 0,
          sold: data.soldCount || 0,
          followers: data.followersCount || 0,
          following: data.followingCount || 0,
          isFollowing: data.isFollowing || false,
          listings: data.listings || [],
        };
      },
      providesTags: (result, error, userId) => [{ type: CACHE_TAGS.SellerProfile, id: userId }],
    }),

    /**
     * Get current user's listings
     */
    getMyListings: builder.query<MyListing[], { status?: 'active' | 'sold' | 'all' }>({
      query: (params) => ({
        url: MARKETPLACE_API.profile.myListings,
        method: 'GET',
        params,
      }),
      transformResponse: (response: ApiResponse<MyListing[]> | null) => {
        if (!response || !response.data) {
          console.warn('getMyListings: Empty or null response from API');
          return [];
        }
        return response.data;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'MyListings' as const, id })),
              { type: CACHE_TAGS.MyListings, id: 'LIST' },
            ]
          : [{ type: CACHE_TAGS.MyListings, id: 'LIST' }],
    }),

    /**
     * Get saved/bookmarked items
     */
    getSavedItems: builder.query<SavedListing[], void>({
      query: () => ({
        url: MARKETPLACE_API.profile.saved,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<SavedListing[]> | null) => {
        if (!response || !response.data) {
          console.warn('getSavedItems: Empty or null response from API');
          return [];
        }
        return response.data;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'SavedItems' as const, id })),
              { type: CACHE_TAGS.SavedItems, id: 'LIST' },
            ]
          : [{ type: CACHE_TAGS.SavedItems, id: 'LIST' }],
    }),

    /**
     * Toggle save/unsave a listing
     */
    toggleSavedItem: builder.mutation<{ saved: boolean }, string>({
      query: (listingId) => ({
        url: MARKETPLACE_API.profile.savedById(listingId),
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<{ saved: boolean }> | null) => {
        if (!response || !response.data) {
          console.warn('toggleSavedItem: Empty or null response from API');
          return { saved: false };
        }
        return response.data;
      },
      invalidatesTags: (result, error, listingId) => [
        { type: CACHE_TAGS.SavedItems, id: 'LIST' },
        { type: CACHE_TAGS.SavedItems, id: listingId },
        { type: CACHE_TAGS.Listing, id: listingId },
      ],
    }),

    /**
     * Get recently viewed listings
     */
    getRecentlyViewed: builder.query<MarketplaceListing[], void>({
      query: () => ({
        url: MARKETPLACE_API.profile.recentlyViewed,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<MarketplaceListing[]> | null) => {
        if (!response || !response.data) {
          console.warn('getRecentlyViewed: Empty or null response from API');
          return [];
        }
        return response.data;
      },
      providesTags: [{ type: CACHE_TAGS.RecentlyViewed, id: 'LIST' }],
    }),

    /**
     * Clear recently viewed listings
     */
    clearRecentlyViewed: builder.mutation<void, void>({
      query: () => ({
        url: MARKETPLACE_API.profile.recentlyViewed,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: CACHE_TAGS.RecentlyViewed, id: 'LIST' }],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetMyProfileQuery,
  useLazyGetMyProfileQuery,
  useGetSellerProfileQuery,
  useLazyGetSellerProfileQuery,
  useGetMyListingsQuery,
  useLazyGetMyListingsQuery,
  useGetSavedItemsQuery,
  useLazyGetSavedItemsQuery,
  useToggleSavedItemMutation,
  useGetRecentlyViewedQuery,
  useLazyGetRecentlyViewedQuery,
  useClearRecentlyViewedMutation,
} = profileApi;

